import {
  // useGetABGroup,
  useGetSessionId,
  useTrackEvent,
} from "@Data-Protocol/event-tracker"
import { useSearchParams } from "@remix-run/react"
import { useCallback, useEffect, useRef, useState } from "react"
import ReactPlayer from "react-player"
import {
  aCurrentCaption,
  aCurrentVideoQuality,
  aDuration,
  aFeedbackData,
  aFeedbackReady,
  aHasPlayed,
  aIsMuted,
  aIsPipMode,
  aIsPlaying,
  aPlaybackSpeed,
  aProgress,
  aSentimentAnswers,
  aVideoQuality,
  aVideoQualityLevelHeights,
  aVolume,
  useAtom,
  useAtomValue,
} from "~/atoms"
import {
  useIsClient,
  useIsControlBarVisible,
  useKeyboardShortcuts,
  usePrefillFeedbackData,
  usePrefillSentimentData,
  useToggleFullscreen,
  useWatchedSeconds,
} from "~/hooks"
import useIsPhone from "~/hooks/useIsPhone"
import { cn } from "~/utils"
import Captions from "./captions"
import { ControlBar, PipModeControl } from "./controls"
import LargePlayControl from "./large-play-pause"
import FeedbackQuestion from "./questions/feedback"
import SentimentQuestions from "./questions/sentiment"
import Spinner from "./spinner"
import TopBanners from "./top-banners"

export default function Player({
  url,
  coverUrl,
  logoRedirectUrl,
  falseEmbed,
  id: videoId,
  showFeedback,
  aiModified,
  createdBy,
  courseId,
}: {
  url: string
  coverUrl: string
  logoRedirectUrl?: string
  falseEmbed?: boolean
  id?: string
  showFeedback?: boolean
  aiModified?: boolean
  createdBy?: string
  courseId?: number
}) {
  useKeyboardShortcuts()
  usePrefillFeedbackData(videoId, showFeedback)
  usePrefillSentimentData(videoId)
  // a number 0-(n-1) which puts the user in an A/B(/C/etc) group
  // const abGroupId = useGetABGroup(2)

  const [searchParams] = useSearchParams()
  const videoRef = useRef<ReactPlayer>(null)
  const containerRef = useRef<HTMLDivElement>(null)
  const [hasPlayed, setHasPlayed] = useAtom(aHasPlayed)
  const [isPlaying, setIsPlaying] = useAtom(aIsPlaying)
  const isMuted = useAtomValue(aIsMuted)
  const volume = useAtomValue(aVolume)
  const [progress, setProgress] = useAtom(aProgress)
  const [duration, setDuration] = useAtom(aDuration)
  const [videoQuality, setVideoQuality] = useAtom(aVideoQuality)
  const currentCaption = useAtomValue(aCurrentCaption)
  const [isBuffering, setIsBuffering] = useState(false)
  const [currentVideoQuality, setCurrentVideoQuality] =
    useAtom(aCurrentVideoQuality)
  const [videoQualityLevelHeights, setVideoQualityLevelHeights] = useAtom(
    aVideoQualityLevelHeights
  )
  const playbackSpeed = useAtomValue(aPlaybackSpeed)
  const isPipMode = useAtomValue(aIsPipMode)
  useWatchedSeconds({ isPlaying, isBuffering })
  const { isControlBarVisible } = useIsControlBarVisible()
  const { isFullscreen, toggleFullscreen } = useToggleFullscreen()
  const setIsPlayingTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(
    null
  )
  const [isHovering, setIsHovering] = useState(false)
  const feedbackData = useAtomValue(aFeedbackData)
  const feedbackReady = useAtomValue(aFeedbackReady)
  const sentimentAnswers = useAtomValue(aSentimentAnswers)
  const sessionId = useGetSessionId()
  const isClient = useIsClient()
  const isPhone = useIsPhone()
  const { trackAction: trackProgress } = useTrackEvent({
    name: "embedded_video",
    type: "player",
    // Call this at most once every 5 seconds on progress change for this URL
    oncePerSeconds: 5,
    unique: isClient ? `${window.location.pathname}-progress` : "progress",
    course_id: courseId,
  })

  const roundTo = useCallback((num: number, places: number) => {
    const factor = 10 ** places
    return Math.round(num * factor) / factor
  }, [])

  // Listen for iframe postMessages
  const [dataFromParent, setDataFromParent] = useState<Record<
    string,
    unknown
  > | null>(null)

  useEffect(() => {
    if (!falseEmbed) {
      window.addEventListener("message", (event) => {
        try {
          setDataFromParent(
              typeof event.data !== "object" ? {
                  ...(JSON.parse(event.data)),
                  courseId: courseId,
              } : event.data
          )
        } catch (e) {
          // console.error("Error parsing message data", e)
        }
      })

      return () => {
        window.removeEventListener("message", () => {})
      }
    } else {
      const url = new URL(window.location.href)
      // This is a fake embed, not using the embed script. We have to initialize the parent data ourselves
      setDataFromParent({
        domain: url.hostname,
        externalId: null,
        "unique-id": "hosted",
        uniqueId: "hosted",
        url: window.location.href,
        url_path: url.pathname,
        "video-id": videoId,
        videoId: videoId,
        courseId: courseId,
        visible: true,
        ...url,
      })
    }
  }, [isClient, falseEmbed, videoId, courseId])

  useEffect(() => {
    // Use video quality to check if the video has loaded
    if (
      currentVideoQuality !== -1 &&
      typeof dataFromParent?.visible === "boolean" &&
      sessionId
    ) {
      trackProgress({
        type: "player",
        action: "player",
        action_meta: {
          courseId: courseId,
          // all settings
          progress: progress ? roundTo(progress, 2) : 0,
          duration: duration ? roundTo(duration, 2) : -1,
          percentage:
            progress && duration ? roundTo((progress / duration) * 100, 2) : 0,
          completed:
            progress && duration
              ? roundTo(progress, 2) > duration * 0.999
              : false,
          volume: volume ? roundTo(volume, 2) : -1,
          caption: currentCaption?.language || "off",
          quality: currentVideoQuality ? `${currentVideoQuality}` : "unknown",
          fullscreen: isFullscreen ? "fullscreen" : "normal",
          picture_in_picture: isPipMode ? "pip" : "normal",
          playbackSpeed: playbackSpeed ? `${playbackSpeed}` : "unknown",
          muted: isMuted ? "muted" : "unmuted",
          playing: isPlaying ? "playing" : "paused",
          feedback:
            feedbackReady || Object.keys(sentimentAnswers).length > 0
              ? {
                  ...(feedbackReady ? { thumbsUpDown: feedbackData } : {}),
                  ...Object.fromEntries(
                    Object.entries(sentimentAnswers).map(([id, value]) => [
                      `sentiment-question-${id}`,
                      value,
                    ])
                  ),
                }
              : undefined,
          ...dataFromParent,
        },
        // Call this at most once every 5 seconds on progress change for this URL
        oncePerSeconds: 5,
        unique: [
          // Do NOT include the progress in the unique key
          // We only want progress to update once every 5 seconds
          "player",
          window.location.pathname,
          dataFromParent?.visible ? "visible" : "hidden",
          volume ? `${roundTo(volume, 2)}` : "unknown",
          currentCaption?.language || "off",
          currentVideoQuality ? `${currentVideoQuality}` : "unknown",
          isFullscreen ? "fullscreen" : "normal",
          isPipMode ? "pip" : "normal",
          playbackSpeed ? `${playbackSpeed}` : "unknown",
          isMuted ? "muted" : "unmuted",
          isPlaying ? "playing" : "paused",
          courseId ? courseId : "no-course-id",
        ].join("-"),
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    progress,
    duration,
    volume,
    currentCaption,
    currentVideoQuality,
    isFullscreen,
    isPipMode,
    playbackSpeed,
    isMuted,
    isPlaying,
    dataFromParent,
    feedbackData,
    feedbackReady,
    sentimentAnswers,
    sessionId,
    courseId,
  ])

  const autoPlay =
    dataFromParent?.autoplay === "true" ||
    dataFromParent?.autoplay === true ||
    searchParams.get("autoplay") === "true"

  useEffect(() => {
    if (autoPlay && isPlaying && !hasPlayed) {
      setHasPlayed(true)
    }
  }, [autoPlay, isPlaying, hasPlayed, setHasPlayed])
  return (
    <div
      ref={containerRef}
      id="player-container"
      className="fixed inset-0 flex w-full items-center justify-center"
    >
      <div
        id="player"
        role="button"
        className="relative aspect-video w-full cursor-default"
        tabIndex={-1}
        onClick={() => {
          if (!isPhone) {
            if (!setIsPlayingTimeoutRef.current) {
              setIsPlayingTimeoutRef.current = setTimeout(() => {
                setIsPlaying(!isPlaying)
                if (!hasPlayed) {
                  setHasPlayed(true)
                }
                setIsPlayingTimeoutRef.current = null
              }, 200)
            }
          } else {
            setIsPlaying(!isPlaying)
            if (!hasPlayed) {
              setHasPlayed(true)
            }
          }
        }}
        onDoubleClick={() => {
          clearTimeout(setIsPlayingTimeoutRef.current || undefined)
          setIsPlayingTimeoutRef.current = null
          toggleFullscreen()
        }}
        onKeyUp={() => {}} // For eslint
      >
        {aiModified && createdBy && (
          <button
            onClick={(e) => e.stopPropagation()}
            className="sr-only focus:not-sr-only focus:absolute focus:left-2 focus:top-2 focus:z-10 focus:rounded focus:bg-black/70 focus:px-1.5 focus:py-0.5 focus:text-[10px] focus:font-medium focus:text-white focus:outline-none"
          >
            {createdBy === "modified-ai"
              ? "Modified with AI"
              : "Created with AI"}
          </button>
        )}
        <div className="absolute inset-0">
          {progress > Math.floor(duration) && (
            <button
              onClick={() => setIsPlaying(true)}
              className="absolute left-0 top-0 z-10 h-full w-full cursor-pointer"
              style={{ padding: 0, border: "none", background: "none" }}
            >
              <img
                src={coverUrl}
                alt="Poster"
                className="h-full w-full object-cover"
              />
            </button>
          )}
          {isClient && (
            <ReactPlayer
              ref={videoRef}
              url={url}
              playing={isPlaying}
              progressInterval={100}
              playbackRate={playbackSpeed}
              muted={isMuted}
              volume={volume}
              playsinline={true}
              autoPlay={autoPlay || undefined}
              config={{
                file: {
                  // forceHLS: true,
                  attributes: {
                    poster: coverUrl,
                    autoPlay: autoPlay ? "true" : undefined,
                  },
                },
              }}
              height="100%"
              width="100%"
              onDuration={(d) => {
                setDuration(d)
              }}
              onBuffer={() => {
                setIsBuffering(true)
              }}
              onBufferEnd={() => {
                setIsBuffering(false)
              }}
              onError={(e) => {
                if (e?.fatal) {
                  console.error("Video error", e)
                }
              }}
              onReady={() => {
                const internalPlayer =
                  videoRef?.current?.getInternalPlayer("hls")
                if (internalPlayer) {
                  const heights: number[] = internalPlayer.levels.map(
                    (level: { height: number }) => level.height
                  )
                  setVideoQualityLevelHeights(heights)
                  if (heights.includes(videoQuality)) {
                    internalPlayer.currentLevel = heights.findIndex(
                      (h) => h === videoQuality
                    )
                    setCurrentVideoQuality(videoQuality)
                  } else {
                    setVideoQuality(-1)
                  }
                }

                // Tell the iframe parent that the video is ready
                if (window.top) {
                  window.top.postMessage("video-ready", "*")
                }
              }}
              onProgress={(p) => {
                setProgress(p.playedSeconds)

                const internalPlayer =
                  videoRef.current?.getInternalPlayer("hls")
                if (internalPlayer) {
                  const height =
                    videoQualityLevelHeights[internalPlayer.currentLevel]
                  if (height && height !== currentVideoQuality) {
                    setCurrentVideoQuality(height)
                  }
                }
              }}
              onEnded={() => setIsPlaying(false)}
              onStart={() => setIsPlaying(true)}
              onPause={() => {
                if (isPlaying) {
                  setIsPlaying(false)
                }
              }}
              onPlay={() => {
                if (!isPlaying) {
                  setIsPlaying(true)
                  if (!hasPlayed) {
                    setHasPlayed(true)
                  }
                }
              }}
            />
          )}
          {!isPipMode && (
            <>
              <Captions
                className={cn(
                  "transition-translate pointer-events-none absolute left-0 right-0 z-10 w-full duration-300",
                  isControlBarVisible || !isPlaying
                    ? "bottom-10 sm:bottom-20 md:bottom-24"
                    : "bottom-4 sm:bottom-12 md:bottom-16"
                )}
              />
              <TopBanners isControlBarVisible={isControlBarVisible} />
              {showFeedback && (
                <FeedbackQuestion isControlBarVisible={isControlBarVisible} />
              )}
              <SentimentQuestions />
            </>
          )}
        </div>
      </div>

      {!isPipMode && (
        <>
          {hasPlayed && (
            <div
              onMouseEnter={() => {
                if (!isPhone) setIsHovering(true)
              }}
              onMouseLeave={() => {
                if (!isPhone) setIsHovering(false)
              }}
              onTouchStart={() => {
                if (isPhone) setIsHovering(true)
              }}
              onTouchEnd={() => {
                if (isPhone) setIsHovering(false)
              }}
            >
              <ControlBar
                logoRedirectUrl={logoRedirectUrl}
                videoRef={videoRef}
                className={cn(
                  "absolute bottom-0 left-0 w-full transition-all duration-300",
                  {
                    "translate-y-full opacity-0":
                      !isControlBarVisible && isPlaying && !isHovering,
                    "translate-y-0 opacity-100":
                      isControlBarVisible || isHovering,
                  }
                )}
              />
            </div>
          )}
          {!isBuffering ? (
            <LargePlayControl
              className={cn(hasPlayed && "-mt-2 sm:mt-0")}
              isAtEnd={progress > Math.floor(duration)}
            />
          ) : (
            <div
              className={cn(
                "pointer-events-none absolute inset-1/2 z-10 h-10 w-10",
                isControlBarVisible && isPhone && "-translate-y-5"
              )}
            >
              <Spinner />
            </div>
          )}

          {hasPlayed && !isFullscreen && (
            <PipModeControl
              className={cn(
                "absolute right-1.5 top-1.5 hidden opacity-0 transition-all sm:right-3 sm:top-3 sm:block"
              )}
            />
          )}
        </>
      )}
    </div>
  )
}
